// E14-t3 → E25-t4 — THE ORGANIZATION OVERVIEW: indicators over the shelf.
//
// The imported DocujointOverview design (2026-08-06, adopted verbatim where
// machinery exists) reads `/` as the ORG'S OWN DASHBOARD: a compact indicator
// row — writes, storage against caps, reports — above a shelf of rich
// knowledge-base cards, each carrying its identity dot, its clone origin in
// mono, thirty days of writes, its three counts, and the referee's health with
// the last push. Everything E14-t3 established survives underneath: one
// listing read, one usage/activity/reports read PER KB into its own cell, and
// a failed cell degrading alone (E14 q2's condition, now also the indicator
// row's: partial sums say they are partial).
//
// WHAT THE DESIGN DRAWS THAT THIS PAGE DOES NOT, on the record:
//   - "Import repository" — nothing imports an external repo; the door
//     arrives with its machinery.
//   - Archive / restore and the archived shelf — no archive state exists.
//   - A MEMBERS indicator — nothing answers "who is in this org"
//     (src/api/admin.ts's own header says so).
//   - The design's RECORDS count — the registry counts FILES; the label says
//     what the number is.
//
// E25-t4 SUPERSEDED E14-t4's whole-card ClickableCard: the design's card
// carries a menu and a link, and a card that is entirely a link cannot hold
// EITHER without nesting interactive content. The NAME is the way in now.
import { useEffect, useRef, useState } from 'react';

import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Grid } from '@astryxdesign/core/Grid';
import { Heading } from '@astryxdesign/core/Heading';
import { Link } from '@astryxdesign/core/Link';
import { MoreMenu } from '@astryxdesign/core/MoreMenu';
import { ProgressBar } from '@astryxdesign/core/ProgressBar';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { Stack } from '@astryxdesign/core/Stack';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';
import { Chart, area, line } from '@astryxdesign/charts';

import type { GateStatus } from '../../../platform/src/guards';
import type { ActivityDay, CreatedKb, KbRow, KbUsage, KbsClient } from '../api/kbs';
import type { ReportRow, ReportsClient } from '../api/reports';
import type { ChipState } from '../components/Chip';
import { Eyebrow } from '../components/Eyebrow';
import { identityColor } from '../identity';
import { messageOf } from '../format/errors';
import { humanBytes, humanCount, relShort } from '../format/units';
import type { Role } from '../shell/AppShell';

import './knowledge-bases.css';

/** The registry read, as one value. */
type Listing =
  | { phase: 'loading' }
  | { phase: 'ready'; rows: KbRow[] }
  | { phase: 'error'; message: string };

/** ONE KB's usage read. Held per KB precisely so that one of them failing is one
 *  card saying so (E14 q2's condition), not a list that never appears. */
type UsageCell =
  | { phase: 'loading' }
  | { phase: 'ready'; usage: KbUsage }
  | { phase: 'error'; message: string };

/** E21-t3 — ONE KB's push-activity read, same shape and same reason. */
type ActivityCell =
  | { phase: 'loading' }
  | { phase: 'ready'; days: ActivityDay[] }
  | { phase: 'error' };

/** E25-t4 — ONE KB's declared reports, for the indicator row alone. */
type ReportsCell =
  | { phase: 'loading' }
  | { phase: 'ready'; reports: ReportRow[] }
  | { phase: 'error' };

interface Draft {
  open: boolean;
  name: string;
  /** the POST is in flight — the button says so and cannot be pressed twice */
  busy: boolean;
  /** the server's own refusal sentence, relayed verbatim */
  error: string | null;
}

const NO_DRAFT: Draft = { open: false, name: '', busy: false, error: null };

/** /:org/:kb is how every request about a KB is addressed, so it is also the key. */
const keyOf = (org: string, name: string) => `${org}/${name}`;

/**
 * WHERE A CARD'S NAME GOES — E14-t4, held by E25.
 *
 * `/:org/:kb` is the knowledge base's WORKSPACE (E25-t2). The exceptions are
 * not rounding errors: `/:org/settings`, `/:org/billing`, `/:org/reports` and
 * `/:org/appearance` are org surfaces pinned by app-routes.test.tsx, so a KB
 * wearing one of those names is sent to `/:org/:kb/dashboards`, which no route
 * can shadow. Exported because src/App.tsx's route comments point here: every
 * org-scoped short url the router adds must be added here in the same change.
 */
export const SHADOWED_KB_NAMES: ReadonlySet<string> = new Set(['settings', 'billing', 'reports', 'appearance']);

export function kbHref(org: string, name: string): string {
  const org_ = encodeURIComponent(org);
  const kb = encodeURIComponent(name);
  return SHADOWED_KB_NAMES.has(name) ? `/${org_}/${kb}/dashboards` : `/${org_}/${kb}`;
}

/** "1 error" / "3 errors". A count with the wrong noun reads as a template. */
const plural = (count: number, one: string, many = `${one}s`): string =>
  `${humanCount(count)} ${count === 1 ? one : many}`;

/** A commit is quoted the way git quotes it back. */
const shortOid = (oid: string): string => oid.slice(0, 7);

/**
 * E15-t3's verdict-to-palette mapping, kept: green→built, red→missing, and the
 * two absences stay unaccusing grey. Exported for the pages that reuse it.
 */
export const VERDICT_CHIP: Record<GateStatus | 'none', ChipState> = {
  green: 'built',
  red: 'missing',
  unknown: 'unspecified',
  none: 'unspecified',
};

export interface KnowledgeBasesProps {
  /** the org NAME — the :org url segment, and what GET /api/kbs rows carry */
  org: string;
  /** the viewer's role IN THIS ORG (platform/src/guards.ts) */
  role: Role;
  /** injected, like every transport in this app */
  client: KbsClient;
  /** the reports indicator's read — optional so callers without it simply get
   *  no reports cell, never an invented zero */
  reports?: ReportsClient;
  /** 2026-08-07 design audit — the create button lives in the ORG HEADER
   *  (Home.tsx), above this component. A bumped counter is its click; the
   *  gate stays here, where `canCreate` is decided. */
  createRequest?: number;
}

export function KnowledgeBases({ org, role, client, reports, createRequest }: KnowledgeBasesProps) {
  const [listing, setListing] = useState<Listing>({ phase: 'loading' });
  const [usage, setUsage] = useState<Record<string, UsageCell>>({});
  const [activity, setActivity] = useState<Record<string, ActivityCell>>({});
  const [declared, setDeclared] = useState<Record<string, ReportsCell>>({});
  const [draft, setDraft] = useState<Draft>(NO_DRAFT);
  /** what POST /api/kbs answered, kept so the clone url can be shown (KB
   *  Creation s1) until the reader dismisses it or creates another. */
  const [created, setCreated] = useState<CreatedKb | null>(null);
  /** bumped after a create — re-reading is cheaper than guessing. */
  const [reload, setReload] = useState(0);

  /** Which per-KB reads have already been STARTED. A ref and not state: the
   *  effect below must not depend on the thing it writes. */
  const asked = useRef<Set<string>>(new Set());

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // THE LIST. One read for the whole session's registry — GET /api/kbs is scoped
  // to the caller's memberships (Tenancy f5) and is not per-org.
  useEffect(() => {
    let live = true;
    setListing({ phase: 'loading' });
    client.listKbs().then(
      (rows) => {
        if (live) setListing({ phase: 'ready', rows });
      },
      (err: unknown) => {
        if (live) setListing({ phase: 'error', message: messageOf(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [client, reload]);

  const rows = listing.phase === 'ready' ? listing.rows.filter((row) => row.org === org) : [];

  // SORTED BY LAST PUSH, the design's own ordering: the verdict names its
  // commit and when it was checked; a KB with no verdict sorts last, in
  // registry order among its kind.
  const shelf = [...rows].sort((a, b) => {
    const at = a.verdict?.checkedAt ?? '';
    const bt = b.verdict?.checkedAt ?? '';
    return bt.localeCompare(at);
  });

  // THE PER-KB READS — usage, activity and (when a client was given) reports,
  // each into its own keyed cell, each free to fail alone.
  useEffect(() => {
    if (listing.phase !== 'ready') return;
    for (const row of rows) {
      const key = keyOf(row.org, row.name);
      if (asked.current.has(key)) continue;
      asked.current.add(key);
      setUsage((current) => ({ ...current, [key]: { phase: 'loading' } }));
      client.loadUsage(row.org, row.name).then(
        (found) => {
          if (mounted.current)
            setUsage((current) => ({ ...current, [key]: { phase: 'ready', usage: found } }));
        },
        (err: unknown) => {
          if (mounted.current)
            setUsage((current) => ({
              ...current,
              [key]: { phase: 'error', message: messageOf(err) },
            }));
        },
      );
      setActivity((current) => ({ ...current, [key]: { phase: 'loading' } }));
      client.loadActivity(row.org, row.name).then(
        (days) => {
          if (mounted.current)
            setActivity((current) => ({ ...current, [key]: { phase: 'ready', days } }));
        },
        () => {
          if (mounted.current)
            setActivity((current) => ({ ...current, [key]: { phase: 'error' } }));
        },
      );
      if (reports) {
        setDeclared((current) => ({ ...current, [key]: { phase: 'loading' } }));
        reports.listReports(row.org, row.name).then(
          (answer) => {
            if (mounted.current)
              setDeclared((current) => ({
                ...current,
                [key]: { phase: 'ready', reports: answer.reports },
              }));
          },
          () => {
            if (mounted.current)
              setDeclared((current) => ({ ...current, [key]: { phase: 'error' } }));
          },
        );
      }
    }
    // `rows` is derived from `listing` and `org`, and `asked` is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing, org, client, reports]);

  // guards.ts: 'command' belongs to admin and maintainer, and `create-kb` rides
  // the kind fallback into exactly that. A viewer is not offered the control.
  const canCreate = role === 'admin' || role === 'maintainer';

  // 2026-08-07 design audit — the org header's button (Home.tsx) opens the
  // draft by bumping this counter; the role gate is the same one as ever.
  useEffect(() => {
    if (!createRequest) return;
    if (canCreate) setDraft({ ...NO_DRAFT, open: true });
    // `canCreate` must not re-open a draft on a role change — the counter is the click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRequest]);

  function submit() {
    const name = draft.name.trim();
    if (name === '' || draft.busy) return;
    setDraft((current) => ({ ...current, busy: true, error: null }));
    client.createKb({ name, org }).then(
      (made) => {
        setCreated(made);
        setDraft(NO_DRAFT);
        asked.current = new Set();
        setUsage({});
        setActivity({});
        setDeclared({});
        setReload((count) => count + 1);
      },
      (err: unknown) => {
        setDraft((current) => ({ ...current, busy: false, error: messageOf(err) }));
      },
    );
  }

  const cells = rows.map((row) => ({
    row,
    usage: usage[keyOf(row.org, row.name)] ?? ({ phase: 'loading' } as UsageCell),
    activity: activity[keyOf(row.org, row.name)] ?? ({ phase: 'loading' } as ActivityCell),
    reports: declared[keyOf(row.org, row.name)] ?? ({ phase: 'loading' } as ReportsCell),
  }));

  return (
    <div role="region" aria-label="Knowledge bases" data-region="knowledge-bases" className="dj-overview">
      {/* KB Creation s1: the clone url is what the person needs NEXT. */}
      {created === null ? null : (
        <Banner
          status="success"
          title={`${created.org}/${created.name} is ready.`}
          description={
            created.gitUrl === null
              ? `Its default branch is ${created.defaultBranch}. Nothing has been pushed to it yet, so there is no verdict for it either.`
              : `Push your documentation to ${created.gitUrl} on ${created.defaultBranch}. The referee runs on that first push, and its answer appears here.`
          }
          container="card"
          isDismissable
          onDismiss={() => setCreated(null)}
        />
      )}

      {/* THE INDICATOR ROW — the org's own numbers, above the shelf. */}
      {listing.phase === 'ready' && rows.length > 0 ? (
        <IndicatorRow cells={cells} hasReports={reports !== undefined} />
      ) : null}

      {/* THE SHELF HEADER — the design's eyebrow line. */}
      <Stack direction="horizontal" gap={2} vAlign="center" hAlign="between" wrap="wrap">
        <Stack direction="horizontal" gap={2} vAlign="center">
          <Eyebrow>Knowledge bases</Eyebrow>
          {listing.phase === 'ready' ? (
            <Text type="code" color="secondary">
              {plural(rows.length, 'knowledge base')} in {org}
            </Text>
          ) : null}
        </Stack>
        {/* 2026-08-07 design audit: the design stamps the ordering whenever the
            shelf shows — one card is still sorted; only the empty state is not.
            The create button moved to the org header (Home.tsx). */}
        {listing.phase === 'ready' && rows.length > 0 ? (
          <Text type="supporting" color="secondary">
            Sorted by last push
          </Text>
        ) : null}
      </Stack>

      {/* THE CREATE FORM — inline rather than a dialog: one field. The design's
          format-template Selector arrives when a second template exists; today
          every KB seeds from the starter template and the sentence says so. */}
      {draft.open ? (
        <Card padding={4} variant="default" elevation="none" data-region="create-knowledge-base">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Stack direction="vertical" gap={3}>
              <Heading level={3} type="display-3">
                New knowledge base in {org}
              </Heading>
              <TextInput
                label="Name"
                value={draft.name}
                onChange={(value) => setDraft((current) => ({ ...current, name: value }))}
                description="A name is a url segment: letters, digits, dots, dashes and underscores, starting with a letter or digit. It cannot end in .git."
                isRequired
                isDisabled={draft.busy}
                width="24rem"
                data-testid="home-kb-name"
              />
              {draft.error === null ? null : (
                <Banner
                  status="error"
                  title="This knowledge base was not created."
                  description={draft.error}
                  container="card"
                />
              )}
              <Stack direction="horizontal" gap={2} wrap="wrap">
                <Button
                  label="Create knowledge base"
                  type="submit"
                  variant="primary"
                  isLoading={draft.busy}
                  isDisabled={draft.name.trim() === ''}
                  data-testid="home-kb-create-submit"
                />
                <Button
                  label="Cancel"
                  variant="ghost"
                  isDisabled={draft.busy}
                  onClick={() => setDraft(NO_DRAFT)}
                />
              </Stack>
              <Text type="supporting" color="secondary">
                Creating one registers it and provisions its repository. Nothing is written into
                it: the documentation arrives by push, and the referee answers on that push.
              </Text>
            </Stack>
          </form>
        </Card>
      ) : null}

      {/* LOADING — the design's skeleton shelf, not a spinner. 2026-08-07
          audit: six placeholders at the ready shelf's own minWidth, a stat-trio
          line per card, and the indicator skeleton above — the loading page
          has the ready page's silhouette. */}
      {listing.phase === 'loading' ? (
        <>
          <IndicatorSkeleton />
          <Grid columns={{ minWidth: 324 }} gap={3} data-state="loading" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i} padding={4} elevation="none">
                <Stack direction="vertical" gap={3}>
                  <Skeleton width="55%" height="13px" index={i} />
                  <Skeleton width="76%" height="10px" index={i} />
                  <Skeleton width="100%" height="40px" index={i} />
                  <Stack direction="horizontal" gap={4}>
                    <Skeleton width="58px" height="10px" index={i} />
                    <Skeleton width="52px" height="10px" index={i} />
                    <Skeleton width="46px" height="10px" index={i} />
                  </Stack>
                  <Skeleton width="100%" height="10px" index={i} />
                </Stack>
              </Card>
            ))}
          </Grid>
        </>
      ) : null}

      {/* A refusal is an ordinary answer, said out loud. */}
      {listing.phase === 'error' ? (
        <Banner
          status="error"
          title="These knowledge bases could not be listed."
          description={listing.message}
          container="card"
          endContent={
            <Button
              label="Try again"
              variant="secondary"
              onClick={() => setReload((count) => count + 1)}
            />
          }
        />
      ) : null}

      {listing.phase === 'ready' && rows.length === 0 && !draft.open ? (
        /* 2026-08-07 design audit: the empty state sits ON a card with its
           vault mark, as the design draws it — the wording stays this page's
           honest one. */
        <Card padding={8} elevation="none">
          <EmptyState
            icon={
              <span
                aria-hidden
                style={{ display: 'block', color: 'var(--color-text-secondary)', opacity: 0.7 }}
              >
                <svg
                  width={38}
                  height={38}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3.5" y="4" width="17" height="16" rx="2" />
                  <circle cx="12" cy="12" r="3.5" />
                  <path d="M12 8.5V6.5M12 17.5v-2M8.5 12h-2M17.5 12h-2" />
                </svg>
              </span>
            }
            title={`${org} has no knowledge bases yet`}
            description={
              canCreate
                ? 'A knowledge base is one git repository of documentation. Create one, push your documents to it, and the referee answers on every push — that answer is what this page shows.'
                : `A knowledge base is one git repository of documentation. As a viewer you can read ${org}’s knowledge bases and capture from them, but creating one is a maintainer’s or an admin’s to do.`
            }
            headingLevel={3}
            actions={
              canCreate ? (
                <Button
                  label="Create the first knowledge base"
                  variant="primary"
                  onClick={() => setDraft({ ...NO_DRAFT, open: true })}
                  data-testid="home-kb-create-first"
                />
              ) : undefined
            }
          />
        </Card>
      ) : null}

      {listing.phase === 'ready' && rows.length > 0 ? (
        <Grid columns={{ minWidth: 324 }} gap={3} role="list" data-region="knowledge-base-list">
          {shelf.map((row) => (
            <KnowledgeBaseCard
              key={keyOf(row.org, row.name)}
              row={row}
              usage={usage[keyOf(row.org, row.name)] ?? { phase: 'loading' }}
              activity={activity[keyOf(row.org, row.name)] ?? { phase: 'loading' }}
            />
          ))}
        </Grid>
      ) : null}
    </div>
  );
}

/** The indicator row's placeholder — shared by the listing's own loading phase
 *  (2026-08-07 audit) and the row's settling phase below. */
function IndicatorSkeleton() {
  return (
    <Card padding={0} elevation="none" aria-hidden="true">
      <div className="dj-indicators" data-state="loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="dj-indicator">
            <Skeleton width="72px" height="9px" index={i} />
            <Skeleton width="86px" height="20px" index={i} />
            <Skeleton width="118px" height="9px" index={i} />
          </div>
        ))}
      </div>
    </Card>
  );
}

/** The whole-window sums, each said only over the cells that ANSWERED —
 *  partial sums say they are partial, the Billing rule at the org's altitude. */
function IndicatorRow({
  cells,
  hasReports,
}: {
  cells: Array<{ row: KbRow; usage: UsageCell; activity: ActivityCell; reports: ReportsCell }>;
  hasReports: boolean;
}) {
  const settledUsage = cells.filter((cell) => cell.usage.phase !== 'loading');
  const settledActivity = cells.filter((cell) => cell.activity.phase !== 'loading');
  const settling = settledUsage.length < cells.length || settledActivity.length < cells.length;

  if (settling) return <IndicatorSkeleton />;

  const activityReady = cells.filter(
    (cell): cell is typeof cell & { activity: { phase: 'ready'; days: ActivityDay[] } } =>
      cell.activity.phase === 'ready',
  );
  const accepted = activityReady.reduce(
    (sum, cell) => sum + cell.activity.days.reduce((s, d) => s + d.accepted, 0),
    0,
  );
  const refused = activityReady.reduce(
    (sum, cell) => sum + cell.activity.days.reduce((s, d) => s + d.refused, 0),
    0,
  );

  const usageReady = cells.filter(
    (cell): cell is typeof cell & { usage: { phase: 'ready'; usage: KbUsage } } =>
      cell.usage.phase === 'ready',
  );
  const bytes = usageReady.reduce((sum, cell) => sum + cell.usage.usage.bytes, 0);
  const capBytes = usageReady.reduce(
    (sum, cell) => sum + (cell.usage.usage.limits?.kbBytes ?? 0),
    0,
  );

  const reportsReady = cells.filter(
    (cell): cell is typeof cell & { reports: { phase: 'ready'; reports: ReportRow[] } } =>
      cell.reports.phase === 'ready',
  );
  const declaredCount = reportsReady.reduce((sum, cell) => sum + cell.reports.reports.length, 0);
  const armedCount = reportsReady.reduce(
    (sum, cell) => sum + cell.reports.reports.filter((r) => r.state === 'armed').length,
    0,
  );

  /** "· K of N answered" whenever a source refused — a partial sum says so. */
  const partial = (ready: number) => (ready < cells.length ? ` · ${ready} of ${cells.length} bases answered` : '');

  return (
    <Card padding={0} elevation="none" data-region="org-indicators">
      <div className="dj-indicators">
        <div className="dj-indicator">
          <Eyebrow>Writes · last 30d</Eyebrow>
          <div className="dj-indicator-figure">
            <span className="dj-indicator-number">{humanCount(accepted)}</span>
            {/* 2026-08-07 audit: a caption, not a cell label — Eyebrow's
                uppercase collapses it into the label motif above it */}
            <Text type="supporting" size="2xs" color="secondary">
              accepted
            </Text>
          </div>
          <Text type="supporting" color="secondary">
            across {plural(cells.length, 'base')} · {humanCount(refused)} rejected by the referee
            {partial(activityReady.length)}
          </Text>
        </div>
        <div className="dj-indicator">
          <Eyebrow>Storage</Eyebrow>
          <div className="dj-indicator-figure">
            <span className="dj-indicator-number">{humanBytes(bytes)}</span>
            {capBytes > 0 ? (
              <Text type="code" color="secondary">
                / {humanBytes(capBytes)}
              </Text>
            ) : null}
          </div>
          {capBytes > 0 ? (
            <ProgressBar label="Storage used" isLabelHidden value={bytes} max={capBytes} />
          ) : (
            <Text type="supporting" color="secondary">
              the caps were not said{partial(usageReady.length)}
            </Text>
          )}
        </div>
        {hasReports ? (
          <div className="dj-indicator">
            <Eyebrow>Reports</Eyebrow>
            <div className="dj-indicator-figure">
              <span className="dj-indicator-number">{humanCount(declaredCount)}</span>
              <Text type="supporting" size="2xs" color="secondary">
                declared
              </Text>
            </div>
            <Text type="supporting" color="secondary">
              {humanCount(armedCount)} armed{partial(reportsReady.length)}
            </Text>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** E21-t3 — THIRTY DAYS OF WRITES, drawn only when the read answered; zero-fill
 *  here because the wire deliberately carries no zero-days. */
function Sparkline({ cell }: { cell: ActivityCell }) {
  if (cell.phase !== 'ready') return null;
  const byDay = new Map(cell.days.map((d) => [d.day, d.accepted]));
  const days = Array.from({ length: 30 }, (_, i) => {
    const day = new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return { day, writes: byDay.get(day) ?? 0 };
  });
  return (
    <div aria-hidden="true" data-sparkline="writes-30d" className="dj-kbcard-spark">
      {/* 2026-08-07 audit: the design's spark — gradient fill under a fine
          accent line, y-domain hugging the data */}
      <Chart
        data={days}
        xKey="day"
        height={40}
        margin={{ top: 5, right: 2, bottom: 2, left: 2 }}
        yBaseline="data"
        series={[
          area('writes', { gradient: true, color: 'var(--color-accent)' }),
          line('writes', { strokeWidth: 1.5, color: 'var(--color-accent)' }),
        ]}
      />
    </div>
  );
}

/** The acceptance rate over the window, or nothing when the read failed —
 *  never a rate about numbers that were not fetched. */
function acceptRateOf(cell: ActivityCell): string | null {
  if (cell.phase !== 'ready') return null;
  const accepted = cell.days.reduce((s, d) => s + d.accepted, 0);
  const refused = cell.days.reduce((s, d) => s + d.refused, 0);
  const total = accepted + refused;
  if (total === 0) return null;
  return `${((accepted / total) * 100).toFixed(1)}% accepted`;
}

/** The short health sentence the design's card bottom carries — the verdict's
 *  own facts, never a paraphrase of ones it does not have. */
function healthOf(row: KbRow): { dot: 'success' | 'error' | 'warning' | 'neutral'; word: string } {
  const verdict = row.verdict;
  if (verdict === null) return { dot: 'neutral', word: 'No verdict yet' };
  if (verdict.status === 'green')
    // 2026-08-07 audit: green WITH warnings wears the warning dot — the words
    // already said it, the colour now agrees. Plain green stays success.
    return verdict.warnings > 0
      ? { dot: 'warning', word: `Green · ${plural(verdict.warnings, 'warning')}` }
      : { dot: 'success', word: 'Referee green' };
  if (verdict.status === 'red') return { dot: 'error', word: `Red · ${plural(verdict.errors, 'error')}` };
  return { dot: 'neutral', word: 'Referee could not judge' };
}

function KnowledgeBaseCard({
  row,
  usage,
  activity,
}: {
  row: KbRow;
  usage: UsageCell;
  activity: ActivityCell;
}) {
  const health = healthOf(row);
  const rate = acceptRateOf(activity);
  const writes =
    activity.phase === 'ready'
      ? activity.days.reduce((sum, day) => sum + day.accepted, 0)
      : null;

  const menu = [
    // 2026-08-07 audit: every card carries a menu, and the workspace is a
    // destination that exists — kbHref is the same door the name-link opens.
    { label: 'Open workspace', onClick: () => location.assign(kbHref(row.org, row.name)) },
    ...(row.gitUrl
      ? [
          {
            label: 'Copy git origin',
            onClick: () => {
              void navigator.clipboard?.writeText(row.gitUrl ?? '');
            },
          },
        ]
      : []),
  ];

  return (
    <Card
      padding={4}
      elevation="none"
      role="listitem"
      data-kb={`${row.org}/${row.name}`}
      data-kbcard=""
      data-verdict={row.verdict?.status ?? 'none'}
    >
      <Stack direction="vertical" gap={3}>
        <Stack direction="horizontal" gap={2} vAlign="start" hAlign="between">
          <Stack direction="vertical" gap={1} className="dj-kbcard-id">
            <Stack direction="horizontal" gap={2} vAlign="center">
              <span
                aria-hidden="true"
                className="dj-kbcard-dot"
                style={{ background: identityColor(row.name) }}
              />
              {/* THE WAY IN — the name is the link (E25 superseded the
                  whole-card target; a card holding a menu cannot be a link). */}
              <Link href={kbHref(row.org, row.name)}>{row.name}</Link>
            </Stack>
            {/* the clone origin, the design's mono second line — the host's own
                string, never assembled; one line, tooltip carrying the rest */}
            <Text type="code" color="secondary" maxLines={1} hasTruncateTooltip>
              {row.gitUrl ?? `default branch ${row.defaultBranch}`}
            </Text>
          </Stack>
          {menu.length > 0 ? (
            <MoreMenu label={`${row.name} actions`} variant="ghost" size="sm" items={menu} />
          ) : null}
        </Stack>

        <Sparkline cell={activity} />

        {/* the design's three counts — FILES is what the registry counts, so
            FILES is the word (the design's RECORDS needs the parse the engine
            does, which this list does not run) */}
        <div className="dj-kbcard-stats">
          <div>
            <span className="dj-kbcard-figure">
              {usage.phase === 'ready' ? humanCount(usage.usage.files) : '—'}
            </span>
            <Eyebrow>Files</Eyebrow>
          </div>
          <div>
            <span className="dj-kbcard-figure">{writes === null ? '—' : humanCount(writes)}</span>
            <Eyebrow>Writes / 30d</Eyebrow>
          </div>
          <div>
            <span className="dj-kbcard-figure">
              {usage.phase === 'ready' ? humanBytes(usage.usage.bytes) : '—'}
            </span>
            <Eyebrow>Storage</Eyebrow>
          </div>
        </div>

        <div className="dj-kbcard-foot">
          {usage.phase === 'error' ? (
            <Stack direction="horizontal" gap={2} vAlign="start">
              <StatusDot variant="error" label="Storage unknown" />
              {/* the server's own sentence — a refused cell is a sentence, not
                  a silent dash */}
              <Text type="code" color="secondary">
                {usage.message}
              </Text>
            </Stack>
          ) : (
            <>
              <Stack direction="horizontal" gap={2} vAlign="center" hAlign="between">
                <Stack direction="horizontal" gap={2} vAlign="center">
                  {/* 2026-08-07 audit: the dot explains itself on hover, with
                      the rate it sits beside when there is one */}
                  <StatusDot
                    variant={health.dot}
                    label={health.word}
                    tooltip={rate ? `${health.word} · ${rate} over the last 30 days` : health.word}
                  />
                  <Text type="supporting" maxLines={1}>
                    {health.word}
                  </Text>
                </Stack>
                {rate ? (
                  <Text type="supporting" color="secondary">
                    {rate}
                  </Text>
                ) : null}
              </Stack>
              {row.verdict?.oid ? (
                <Stack direction="horizontal" gap={2} vAlign="center">
                  <Eyebrow>Pushed</Eyebrow>
                  {/* 2026-08-07 audit: relative_short is the design's grammar
                      here — relShort (units.ts) argues why Timestamp is not */}
                  <Text type="code" color="secondary">
                    {shortOid(row.verdict.oid)}
                    {row.verdict.checkedAt ? ` · ${relShort(row.verdict.checkedAt)}` : ''}
                  </Text>
                </Stack>
              ) : null}
            </>
          )}
        </div>
      </Stack>
    </Card>
  );
}

export default KnowledgeBases;
